import type { ReactNode } from 'react';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationSummary } from '../../types';
import { Navbar } from './Navbar';
import { SideMenu } from './SideMenu';

interface LayoutProps {
  children: ReactNode;
  userEmail?: string;
  onSignOut: () => void;
  onNewChat?: () => void;
  recentConversations?: ConversationSummary[];
  onSelectConversation?: (chatId: string) => void;
  currentChatId?: string;
}

export const Layout = ({
  children,
  userEmail,
  onSignOut,
  onNewChat,
  recentConversations,
  onSelectConversation,
  currentChatId,
}: LayoutProps) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNewChat = useCallback(() => {
    setIsMenuOpen(false);
    if (onNewChat) {
      onNewChat();
    } else {
      // Default behavior: navigate to home for a new chat
      navigate('/');
    }
  }, [onNewChat, navigate]);

  const handleSelectConversation = useCallback(
    (chatId: string) => {
      setIsMenuOpen(false);
      if (onSelectConversation) {
        onSelectConversation(chatId);
      } else {
        // Default behavior: navigate to the chat
        navigate(`/chats/${chatId}`);
      }
    },
    [onSelectConversation, navigate],
  );

  return (
    <div className="safe-area-inset relative flex h-[100dvh] w-full flex-col bg-background overflow-hidden">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Side Menu */}
      <SideMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        userEmail={userEmail}
        onSignOut={onSignOut}
        onNewChat={handleNewChat}
        recentConversations={recentConversations}
        onSelectConversation={handleSelectConversation}
        currentChatId={currentChatId}
      />

      {/* Top App Bar */}
      <Navbar
        onMenuClick={() => setIsMenuOpen(true)}
        onNewChat={handleNewChat}
      />

      {/* Main Content */}
      <main
        id="main-content"
        className="flex flex-1 flex-col overflow-hidden w-full"
        tabIndex={-1}
      >
        {children}
      </main>
    </div>
  );
};
